import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchUsers, fetchUsersLite, updateUserRole, updateUserRate, deleteUser, fetchEntries as apiFetchEntries, updateEntry as apiUpdateEntry, deleteEntry, reassignEntries, fetchGroups, createGroup, deleteGroup, fetchMatters, fetchTags, createInvite, fetchInvites, deleteInvite, adminResetUserPassword } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { Copy, Trash2, Key, DollarSign } from "lucide-react";
import BackupsTab from "../components/BackupsTab";
import { todayISO, ndaysAgoISO } from "../lib/dates";

export default function AdminPage(){
  const { user } = useAuth();
  const { toast } = useToast();
  const isSuper = user?.role === 'superadmin';
  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');

  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [matters, setMatters] = useState([]);
  const [tags, setTags] = useState([]);
  const [range, setRange] = useState("30");
  
  // Password reset dialog state
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  
  // Rate update dialog state
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateDialogUser, setRateDialogUser] = useState(null);
  const [newRate, setNewRate] = useState("");
  
  const [targetUser, setTargetUser] = useState("");
  const [selected, setSelected] = useState({});
  const userMap = React.useMemo(() => {
    const m = {}; (users||[]).forEach(u => { m[u.id] = u.email || u.name || u.id; }); return m;
  }, [users]);
  const matterMap = React.useMemo(() => {
    const m = {}; (matters||[]).forEach(mm => { m[mm.id] = mm.name; }); return m;
  }, [matters]);

  useEffect(() => {
    if (isSuper) {
      fetchUsers().then(setUsers).catch(()=>{});
    } else if (isAdmin) {
      fetchUsersLite().then(setUsers).catch(()=>{});
    }
  }, [isSuper, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    let from = ndaysAgoISO(29), to = todayISO();
    if (range === 'all') { from = '1970-01-01'; }
    else { const n = parseInt(range, 10); if (!isNaN(n) && n > 0) from = ndaysAgoISO(n-1); }
    Promise.all([
      apiFetchEntries({ from, to }),
      fetchMatters(),
      fetchTags()
    ]).then(([es, ms, ts]) => { setEntries(es); setMatters(ms); setTags(ts); }).catch(()=>{});
  }, [isAdmin, range]);

  async function changeRole(uid, role){
    await updateUserRole(uid, role);
    setUsers(users.map(u => u.id === uid ? { ...u, role } : u));
  }

  function openRateDialog(userObj) {
    setRateDialogUser(userObj);
    setNewRate(userObj.rate ? String(userObj.rate) : "");
    setRateDialogOpen(true);
  }

  async function handleUpdateRate() {
    const rateValue = newRate.trim() === "" ? null : parseFloat(newRate);
    if (rateValue !== null && (isNaN(rateValue) || rateValue < 0)) {
      toast({ variant: "destructive", title: "Rate must be a positive number" });
      return;
    }
    try {
      await updateUserRate(rateDialogUser.id, rateValue);
      setUsers(users.map(u => u.id === rateDialogUser.id ? { ...u, rate: rateValue } : u));
      toast({ title: `Rate updated for ${rateDialogUser.email}` });
      setRateDialogOpen(false);
      setNewRate("");
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error updating rate" });
    }
  }

  function toggle(id){ setSelected(s => ({ ...s, [id]: !s[id] })); }

  async function performReassign(){
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (!ids.length || !targetUser) return;
    await reassignEntries(ids, targetUser);
    setEntries(prev => prev.map(e => ids.includes(e.id) ? { ...e, userId: targetUser } : e));
    setSelected({});
  }

  async function handleDeleteEntry(entryId) {
    if (!window.confirm("Are you sure you want to delete this entry? This action cannot be undone.")) return;
    try {
      await deleteEntry(entryId);
      setEntries(prev => prev.filter(e => e.id !== entryId));
      toast({ title: "Entry deleted successfully" });
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error deleting entry" });
    }
  }

  function openResetPassword(userObj) {
    setResetPasswordUser(userObj);
    setNewPassword("");
    setResetPasswordOpen(true);
  }

  async function handleResetPassword() {
    if (!newPassword || newPassword.length < 6) {
      toast({ variant: "destructive", title: "Password must be at least 6 characters" });
      return;
    }
    try {
      await adminResetUserPassword(resetPasswordUser.id, newPassword);
      toast({ title: `Password reset for ${resetPasswordUser.email}` });
      setResetPasswordOpen(false);
      setNewPassword("");
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error resetting password" });
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <h1 className="text-xl font-semibold mb-4">Admin</h1>
      <Tabs defaultValue={isSuper ? "users" : "entries"}>
        <TabsList>
          {isSuper && <TabsTrigger value="users">Users</TabsTrigger>}
          <TabsTrigger value="entries">Entries</TabsTrigger>
          <TabsTrigger value="groups">Groups</TabsTrigger>
          <TabsTrigger value="invites">Invites</TabsTrigger>
          {isSuper && <TabsTrigger value="backups">Backups</TabsTrigger>}
        </TabsList>

        {isSuper && (
          <TabsContent value="users" className="mt-4">
            <div className="rounded border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Hourly Rate</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id}>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <Select value={u.role || 'user'} onValueChange={(v)=>changeRole(u.id, v)}>
                          <SelectTrigger className="w-40"><SelectValue placeholder="Role"/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="superadmin">Super admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{u.rate ? `$${u.rate}` : '—'}</span>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => openRateDialog(u)}
                            title="Set hourly rate"
                            className="h-8 w-8 p-0"
                          >
                            <DollarSign className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => openResetPassword(u)}
                            title="Reset password"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={async ()=>{
                            if(!confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
                            await deleteUser(u.id);
                            setUsers(prev => prev.filter(x=>x.id!==u.id));
                          }}>Delete</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}

        <TabsContent value="entries" className="mt-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Range</span>
              <Select value={range} onValueChange={setRange}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Range"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="180">Last 6 months</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2 ml-auto">
                <Select value={targetUser || ""} onValueChange={setTargetUser}>
                  <SelectTrigger className="w-[220px]"><SelectValue placeholder="Reassign to user"/></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={performReassign} disabled={!Object.values(selected).some(Boolean) || !targetUser}>Reassign</Button>
              </div>
            )}
          </div>
          {isSuper && (
            <div className="mb-3 flex justify-end">
              <Button variant="secondary" onClick={async ()=>{ try { const res = await import("../lib/api").then(m=>m.downloadEntriesBackup()); const blob = new Blob([res.data], { type: 'application/zip' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `hourslaw_admin_entries_${new Date().toISOString().slice(0,10)}.zip`; a.click(); URL.revokeObjectURL(url); } catch(e){ console.error(e); } }}>Download entries (CSV in ZIP)</Button>
            </div>
          )}


          <div className="rounded border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead className="w-8">Sel</TableHead>}
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Matter</TableHead>
                  <TableHead>Task codes</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>User</TableHead>
                  {isSuper && <TableHead className="w-16">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(e => (
                  <TableRow key={e.id}>
                    {isAdmin && <TableCell><input type="checkbox" checked={!!selected[e.id]} onChange={()=>toggle(e.id)} /></TableCell>}
                    <TableCell>
                      <InlineDate value={e.date} onChange={async (v)=>{ const saved = await apiUpdateEntry(e.id, { date: v }); setEntries(prev => prev.map(x => x.id===e.id ? saved : x)); }}/>
                    </TableCell>
                    <TableCell>
                      <InlineText value={e.description} onChange={async (v)=>{ const saved = await apiUpdateEntry(e.id, { description: v }); setEntries(prev => prev.map(x => x.id===e.id ? saved : x)); }} placeholder="Description"/>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <InlineProject value={e.projectId || ''} onChange={async (v)=>{ const saved = await apiUpdateEntry(e.id, { projectId: v || null }); setEntries(prev => prev.map(x => x.id===e.id ? saved : x)); }} projects={matters}/>
                    </TableCell>
                    <TableCell>
                      <InlineMultiTag values={e.tagIds||[]} tags={tags} onChange={async (vals)=>{ const saved = await apiUpdateEntry(e.id, { tagIds: vals }); setEntries(prev => prev.map(x => x.id===e.id ? saved : x)); }}/>
                    </TableCell>
                    <TableCell>
                      <InlineNumber value={e.decimalHours ?? (e.durationTenths/10)} onChange={async (v)=>{ const saved = await apiUpdateEntry(e.id, { decimalHours: v }); setEntries(prev => prev.map(x => x.id===e.id ? saved : x)); }}/>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.userEmail || userMap[e.userId] || e.user || e.userId || '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.user || e.userId || '—'}</TableCell>
                    {isSuper && (
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDeleteEntry(e.id)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="groups" className="mt-4">
          <GroupsTab/>
        </TabsContent>

        <TabsContent value="invites" className="mt-4">
          <InvitesTab/>
        </TabsContent>

        {isSuper && (
          <TabsContent value="backups" className="mt-4">
            <BackupsTab />
          </TabsContent>
        )}

      </Tabs>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password for {resetPasswordUser?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword}>
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Update Dialog */}
      <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Hourly Rate for {rateDialogUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="hourlyRate">Hourly Rate ($/hour)</Label>
              <Input
                id="hourlyRate"
                type="number"
                step="0.01"
                min="0"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                placeholder="Enter hourly rate (e.g., 350.00)"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used for lodestar calculations in reports
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRate}>
              Update Rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GroupsTab(){
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState("");
  const [matters, setMatters] = useState([]);
  const [selectedMatterIds, setSelectedMatterIds] = useState([]);

  useEffect(() => {
    fetchGroups().then(setGroups).catch(()=>{});
    fetchMatters().then(setMatters).catch(()=>{});
  }, []);

  function toggleMatter(id){
    setSelectedMatterIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  }

  async function add(){
    if (!name || selectedMatterIds.length===0) return;
    const created = await createGroup({ name, matterIds: selectedMatterIds });
    setGroups(prev => [created, ...prev]);
    setName(""); setSelectedMatterIds([]);
  }

  async function remove(id){
    await deleteGroup(id);
    setGroups(prev => prev.filter(g => g.id !== id));
  }

  return (
    <div className="space-y-4">
      <div className="rounded border p-3">
        <div className="text-sm font-medium mb-2">Create group</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Name</div>
            <input value={name} onChange={(e)=>setName(e.target.value)} className="border rounded px-2 py-1 text-sm" placeholder="Group name"/>
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className="text-xs text-muted-foreground mb-1">Matters</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-auto border rounded p-2">
              {matters.map(m => (
                <label key={m.id} className="text-sm flex items-center gap-2">
                  <input type="checkbox" checked={selectedMatterIds.includes(m.id)} onChange={()=>toggleMatter(m.id)} />
                  <span>{m.name}</span>
                </label>
              ))}
            </div>
          </div>
          <Button onClick={add} disabled={!name || selectedMatterIds.length===0}>Create</Button>
        </div>
      </div>

      <div className="rounded border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Matters</TableHead>
              <TableHead className="w-28"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map(g => (
              <TableRow key={g.id}>
                <TableCell>{g.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{(g.matterIds||[]).length} selected</TableCell>
                <TableCell className="text-right"><Button variant="destructive" size="sm" onClick={()=>remove(g.id)}>Delete</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function InvitesTab(){
  const [email, setEmail] = useState("");
  const [invites, setInvites] = useState([]);
  const { toast } = require("../hooks/use-toast");

  useEffect(()=>{ fetchInvites().then(setInvites).catch(()=>{}); },[]);

  async function add(){
    if (!email.trim()) return;
    const inv = await createInvite(email.trim());
    setInvites(prev => [inv, ...prev]);
    setEmail("");
  }

  async function remove(id){
    await deleteInvite(id);
    setInvites(prev => prev.filter(i => i.id !== id));
  }

  return (
    <div className="space-y-4">
      <div className="rounded border p-3">
        <div className="text-sm font-medium mb-2">Create invitation</div>
        <div className="flex items-end gap-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Email</div>
            <input value={email} onChange={(e)=>setEmail(e.target.value)} className="border rounded px-2 py-1 text-sm" placeholder="name@org.com"/>
          </div>
          <Button onClick={add} disabled={!email.trim()}>Generate code</Button>
        </div>
      </div>

      <div className="rounded border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Used</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-28"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.map(i => (
              <TableRow key={i.id}>
                <TableCell className="font-mono text-sm flex items-center gap-2">
                  <span>{i.code}</span>
                  <Button size="icon" variant="ghost" onClick={()=>{ navigator.clipboard.writeText(i.code); }} title="Copy code">
                    <Copy className="size-4"/>
                  </Button>
                </TableCell>
                <TableCell>{i.email || '—'}</TableCell>
                <TableCell>{i.used ? 'Yes' : 'No'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{i.createdAt?.slice(0,19).replace('T',' ')}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={()=>remove(i.id)}>Delete</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function InlineDate({ value, onChange }) {
  const [val, setVal] = React.useState(value || "");
  React.useEffect(() => setVal(value || ""), [value]);
  return (
    <input
      type="date"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => onChange(val)}
      className="w-full bg-transparent outline-none border-b border-transparent focus:border-border transition-colors text-sm py-1"
    />
  );
}

function InlineText({ value, onChange, placeholder }) {
  const [val, setVal] = React.useState(value || "");
  React.useEffect(() => setVal(value || ""), [value]);
  return (
    <input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => onChange(val)}
      placeholder={placeholder}
      className="w-full bg-transparent outline-none border-b border-transparent focus:border-border transition-colors text-sm py-1"
    />
  );
}

function InlineProject({ value, onChange, projects }) {
  return (
    <Select value={(value || "none")} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger className="w-full max-w-sm">
        <SelectValue placeholder="Select Matter"/>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">No matter</SelectItem>
        {projects.map(p => (
          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function InlineNumber({ value, onChange }) {
  const [val, setVal] = React.useState(String(value ?? ""));
  React.useEffect(() => setVal(String(value ?? "")), [value]);
  
  function commit() {
    const num = parseFloat(String(val).replace(/,/g, "."));
    if (isNaN(num) || num < 0) return;
    onChange(Math.round(num * 10) / 10);
  }
  
  return (
    <input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      className="w-24 text-right bg-transparent outline-none border-b border-transparent focus:border-border transition-colors text-sm py-1 tabular-nums"
      inputMode="decimal"
    />
  );
}

function InlineMultiTag({ values, tags, onChange }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedTags = (values || []).map(id => tags.find(t => t.id === id)).filter(Boolean);
  
  function toggleTag(tagId) {
    const newValues = values || [];
    if (newValues.includes(tagId)) {
      onChange(newValues.filter(id => id !== tagId));
    } else {
      onChange([...newValues, tagId]);
    }
  }
  
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="h-auto p-1 text-xs"
      >
        {selectedTags.length > 0 ? `${selectedTags.length} tags` : "Add tags"}
      </Button>
      {isOpen && (
        <div className="absolute z-10 mt-1 w-48 bg-white border rounded shadow-lg max-h-48 overflow-auto">
          {tags.map(tag => (
            <label key={tag.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={(values || []).includes(tag.id)}
                onChange={() => toggleTag(tag.id)}
              />
              <span className="text-sm">{tag.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
